'use client';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Search, User } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useMe } from '../../lib/queries';
import { useSignOut } from '../../lib/mutations';
import { PageTopBarHost, usePageHeader } from '../../lib/pageHeader';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { SkinSwitcher } from '../ui/SkinSwitcher';
import { useElementWidth } from '../../lib/useElementWidth';
import { usePaletteShortcutHint } from '../../lib/shortcuts';
import { COMMAND_PALETTE_OPEN_EVENT } from './CommandPalette';
import { ShellLink } from './ShellLink';
import { AlertsBell } from './AlertsBell';

/** Below this actual bar width the shell hides secondary labels and actions. */
const CHAT_BAR_WIDE_MIN = 1150;

/** The shell's ruled page bar, outside the page scroller at every width.
 * Its height and hairline share the sidebar tokens; route controls mount in the central slot. */
export function TopBar({ onMenuClick, onNavToggle, navCollapsed = false, navSide = 'left', showLocation = true }: {
  onMenuClick?: () => void;
  onNavToggle?: () => void;
  navCollapsed?: boolean;
  navSide?: 'left' | 'right';
  showLocation?: boolean;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const paletteHint = usePaletteShortcutHint();
  const pageHeader = usePageHeader();
  const { signOut, isPending: signingOut } = useSignOut();
  const { title, count, icon: Icon } = pageHeader?.header ?? {};
  // Not rendered rather than hidden, so the bar's focus order skips them too.
  const secondaryActions = !pageHeader?.secondaryActionsHidden;
  const [barRef, barWidth, barMeasured] = useElementWidth<HTMLElement>();
  const chatControlsNarrow = !barMeasured || barWidth <= CHAT_BAR_WIDE_MIN;
  const NavOpenIcon = navSide === 'right' ? PanelRightOpen : PanelLeftOpen;
  const NavCloseIcon = navSide === 'right' ? PanelRightClose : PanelLeftClose;

  return (
    <header ref={barRef} data-testid="future-page-header"
      data-chat-controls-narrow={chatControlsNarrow || undefined}
      className="top-bar top-bar--bar sticky top-0 z-[var(--z-popover)] flex h-[var(--topbar-height)] shrink-0 items-center gap-3 border-b border-sidebar-border bg-background px-4">
      <div className="top-bar__leading flex min-w-0 flex-1 items-center gap-2">
        {onMenuClick ? (
          <Button variant="ghost" size="icon" type="button" onClick={onMenuClick}
            aria-label={t.common.toggleSidebar} className="top-bar__menu -ml-1">
            <Menu size={18} strokeWidth={1.5} aria-hidden />
          </Button>
        ) : null}
        {onNavToggle ? (
          <Button variant="ghost" size="icon" type="button" onClick={onNavToggle}
            data-testid="top-bar-nav-collapse" data-nav-side={navSide}
            aria-label={navCollapsed ? t.common.expandNav : t.common.collapseNav}
            title={`${navCollapsed ? t.common.expandNav : t.common.collapseNav} · ${t.nav.collapseShortcut}`}
            aria-keyshortcuts="Control+Backslash Meta+Backslash" className="top-bar__nav-toggle">
            {navCollapsed ? <NavOpenIcon size={20} strokeWidth={1.5} aria-hidden /> : <NavCloseIcon size={20} strokeWidth={1.5} aria-hidden />}
          </Button>
        ) : null}
        {showLocation && title ? (
          <div className="top-bar__location flex min-w-0 items-center gap-2">
            {Icon ? <Icon size={18} strokeWidth={1.5} className="shrink-0 max-[400px]:hidden" aria-hidden /> : null}
            <span className="truncate text-ui text-foreground">{title}</span>
            {count !== undefined ? <span className="text-small text-muted-foreground max-[400px]:hidden">{count}</span> : null}
          </div>
        ) : null}
      </div>
      <PageTopBarHost className="top-bar__page-slot flex min-w-0 items-center justify-center empty:hidden" />
      <div className="top-bar__actions ml-auto flex shrink-0 items-center gap-0.5">
        {secondaryActions ? (
          <Button variant="ghost" size="icon" type="button"
            onClick={() => window.dispatchEvent(new Event(COMMAND_PALETTE_OPEN_EVENT))}
            aria-label={t.common.openCommandPalette} title={`${t.common.openCommandPalette} · ${paletteHint}`}
            aria-keyshortcuts="Control+K Meta+K"
            className="top-bar__search group flex items-center justify-center max-[767px]:hidden">
            <Search size={18} strokeWidth={1.5} aria-hidden />
          </Button>
        ) : null}
        {me.data?.user ? <AlertsBell /> : null}
        {me.data?.user ? (
          <Button variant="ghost" size="icon" type="button" onClick={signOut} disabled={signingOut}
            aria-label={t.common.logout} title={t.common.logout}
            className="top-bar__logout flex items-center justify-center disabled:opacity-50 max-[767px]:hidden">
            <LogOut size={18} strokeWidth={1.5} aria-hidden />
          </Button>
        ) : null}
        {secondaryActions ? <SkinSwitcher collapsed /> : null}
        {secondaryActions ? <LanguageSwitcher collapsed={Boolean(onMenuClick)} /> : null}
        {/* The account's OWN card, always: `/account` would reopen whatever section this browser last
            visited (API tokens, notifications), and "change my name" must not start somewhere else. The
            account window still remembers its section for movement inside it, and `?cat=` wins over it. */}
        <ShellLink href="/account?cat=profile"
          className={`top-bar__identity ml-0.5 flex items-center rounded-full ring-ring/30 transition-[opacity,box-shadow] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 ${onMenuClick ? 'min-h-[var(--touch-target)] min-w-[var(--touch-target)] justify-center' : ''}`}
          title={me.data?.user ? (me.data.user.name || me.data.user.username) : t.common.daemon}>
          {me.data?.user ? <Avatar user={me.data.user} size={26} /> : (
            <span className="flex size-6.5 items-center justify-center rounded-full border border-border bg-muted"><User size={15} className="text-muted-foreground" aria-hidden /></span>
          )}
        </ShellLink>
      </div>
    </header>
  );
}
